import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { AbstractComponent } from '@shared/common/abstract.component';
import { ConfirmDialogComponent } from '@shared/dialogs/confirm/confirm-dialog.component';

import { makeProvider } from '@shared/form-fields/make-provider';
import type { FixedRate } from '@shared/modules/fixed-rates/fixed-rate';
import { AppSnackBar } from '@shared/services/snackbar/app-snackbar.service';
import { clone } from '@shared/utils';
import { equals, isPresent } from '@shared/utils/helpers';
import { v4 as uuid } from 'uuid';

export interface FixedRateModel extends FixedRate {
    is_default?: boolean;
}

@Component({
    selector: 'app-fixed-rates',
    templateUrl: './fixed-rates.component.html',
    styleUrls: ['./fixed-rates.component.scss'],
    providers: [makeProvider(FixedRatesComponent)],
})
export class FixedRatesComponent extends AbstractComponent {

    _rates: FixedRate[];
    _rateModels: FixedRateModel[];
    private _defaultRateId: string;

    @Input() set rates(value: FixedRate[]) {
        this._rates = value;

        const inputRateModels = clone(value)?.map(rate => ({ ...rate }));

        const draftRateModels = this.getDraftRateModels(this._rateModels, inputRateModels);

        this._rateModels = inputRateModels.concat(draftRateModels);

        this._loadDefault();
    }

    @Output() ratesChange = new EventEmitter<FixedRate[]>();
    @Input() disabled = false;
    @Input() hint: string;

    @Input() set default(value: string) {
        this._defaultRateId = value;
        if (value) {
            this._loadDefault();
        }
    }

    @Output() defaultChange = new EventEmitter<string>();

    ui = {
        deleting: false,
    };

    constructor(
        private snackBar: AppSnackBar,
        private dialog: MatDialog,
    ) {
        super();
    }

    _loadDefault() {
        if (Array.isArray(this._rateModels)) {
            if (this._defaultRateId) {
                this._rateModels.forEach(rate => {
                    rate.is_default = rate.id === this._defaultRateId;
                });
            } else {
                this._rateModels.forEach(rate => {
                    rate.is_default = false;
                });
            }
        }
    }

    addRule(rate: FixedRate) {
        if (!rate) {
            return;
        }

        let rules = rate.rules;

        if (!Array.isArray(rules)) {
            rules = [];
        }

        rules.push({
            name: null,
            actions: [],
            conditions: [],
        });

        rate.rules = rules;
    }

    addEmptyRate() {
        if (!Array.isArray(this._rateModels)) {
            this._rateModels = [];
        }

        this._rateModels.push({
            id: uuid(),
            name: null,
            amount: null,
        });

        this._onModelChanged();
    }

    deleteRate($event, rate: FixedRateModel) {
        if (this.ui.deleting) {
            return;
        }

        this.ui.deleting = true;

        ConfirmDialogComponent.show(this.dialog, {
            title: 'Delete fixed rate',
            content: 'Are you sure you want to continue?',
            okay: 'Delete',
        }).afterClosed()
            .subscribe((result: any) => {
                if (result) {
                    const index = this._rateModels.indexOf(rate);

                    this._rateModels.splice(index, 1);

                    this._onModelChanged();
                }

                this.ui.deleting = false;
            });
    }

    onDefaultChanged(rate: FixedRateModel, isDefault: boolean) {
        if (isDefault) {
            this.default = rate.id;
        } else {
            this.default = null;
        }

        this.defaultChange.emit(this._defaultRateId);

        this._onModelChanged();
    }

    _onModelChanged() {
        const updatedRates: FixedRate[] = this._rateModels
            .filter(model => model.name && isPresent(model.amount))
            .map((model: FixedRateModel) => {
                const m = clone(model);

                delete m.is_default;

                return m;
            });

        const hasChanged = !equals(this._rates, updatedRates);

        if (hasChanged) {
            this.ratesChange.emit(updatedRates);
        }
    }

    _trackFixedRateFn(index: number, rate: FixedRate) {
        return rate.id ?? rate.name;
    }

    private getDraftRateModels(existingRates, inputRates) {
        if (!existingRates) {
            return [];
        }

        return existingRates.filter(existingRate => {
            const matches = inputRates.filter(
                inputRate => inputRate.name === existingRate.name && inputRate.amount === existingRate.amount
            ).length;

            return matches === 0;
        });
    }
}
